import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Rocket, CheckCircle2, AlertTriangle, Clock, TrendingUp,
  ShieldAlert, ChevronRight, BarChart3,
} from 'lucide-react';

/**
 * Deployment Insights — embedded on the Apps Dashboard.
 *
 * Data source: GET /api/analytics/deployment-insights
 *  - kpis: success rate, devices, deployments in window
 *  - deployments_per_day: 30-day trend
 *  - failure_breakdown: per-category failure counts (Intune error codes
 *    pre-classified by the hourly scheduler)
 *  - apps_by_failure_rate: top offenders (with >=3 attempts)
 *
 * No LLM, no live Graph calls — the scheduler populates
 * `deployment_failure_details` hourly so this view always renders fast.
 */

const ACCENT = {
  emerald: '#10B981',
  rose:    '#F43F5E',
  amber:   '#F59E0B',
  cyan:    '#00D4E4',
  purple:  '#8B5CF6',
  slate:   '#94A3B8',
};

const KPI = ({ label, value, sub, icon: Icon, accent, testid }) => (
  <Card
    className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]"
    data-testid={testid}
  >
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs font-medium uppercase tracking-wider text-white/80">
          {label}
        </span>
        {Icon && (
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${accent}20` }}
          >
            <Icon className="w-4 h-4" style={{ color: accent }} />
          </div>
        )}
      </div>
      <div className="text-3xl font-bold text-white leading-none">{value}</div>
      {sub && <div className="text-xs text-white/60 mt-2">{sub}</div>}
    </CardContent>
  </Card>
);

// 30-day sparkline / bar trend. Square-root scaling so a single busy day
// doesn't flatten the rest of the chart.
const TrendBars = ({ series }) => {
  const max = Math.max(1, ...series.map((d) => d.count));
  const scale = (v) => (v <= 0 ? 0 : Math.sqrt(v) / Math.sqrt(max));
  return (
    <div
      className="flex items-end gap-[3px] h-40"
      data-testid="deployment-trend-bars"
    >
      {series.map((d, i) => {
        const h = Math.max(2, Math.round(scale(d.count) * 100));
        return (
          <div
            key={d.date || i}
            className="flex-1 rounded-t-sm transition-colors"
            style={{
              height: `${h}%`,
              background: d.count
                ? 'linear-gradient(180deg, #00D4E4 0%, #6376F0 100%)'
                : 'rgba(255,255,255,0.08)',
              minWidth: 4,
            }}
            title={`${d.date}: ${d.count}`}
          />
        );
      })}
    </div>
  );
};

const FailureBar = ({ item, max, onClick }) => {
  const pct = max ? Math.round((item.count / max) * 100) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left group"
      data-testid={`failure-bar-${item.category}`}
    >
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-white/90 flex items-center gap-2">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: item.color }}
          />
          {item.label}
        </span>
        <span className="text-white/70">
          {item.count} · {item.percent}%
        </span>
      </div>
      <div className="h-2 bg-[rgba(229,239,250,0.08)] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: item.color }}
        />
      </div>
    </button>
  );
};

const DeploymentInsights = () => {
  const navigate = useNavigate();
  const { getAccessToken, demoMode } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = {};
        if (!demoMode) {
          const token = await getAccessToken();
          if (token) headers.Authorization = `Bearer ${token}`;
        }
        const res = await axios.get(
          `${API}/analytics/deployment-insights`,
          { headers, params: { window_days: 30 } },
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        console.error('DeploymentInsights load failed', e);
        if (!cancelled) setError('Could not load deployment insights.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [getAccessToken, demoMode]);

  const failureMax = useMemo(() => {
    if (!data?.failure_breakdown?.length) return 0;
    return Math.max(...data.failure_breakdown.map((f) => f.count));
  }, [data]);

  if (loading) {
    return (
      <div
        className="mt-8 flex items-center justify-center min-h-[200px]"
        data-testid="deployment-insights-loading"
      >
        <div className="w-10 h-10 rounded-full border-4 border-[rgba(229,239,250,0.1)] border-t-cyan-400 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="mt-8 text-white/70 text-sm"
        data-testid="deployment-insights-error"
      >
        {error || 'No deployment insights available yet.'}
      </div>
    );
  }

  const k = data.kpis || {};
  const series = data.deployments_per_day || [];
  const breakdown = data.failure_breakdown || [];
  const offenders = data.apps_by_failure_rate || [];

  const topFailureLabel = breakdown.length
    ? `${breakdown[0].label} · ${breakdown[0].count}`
    : 'No failures tracked';

  return (
    <section className="mt-10" data-testid="deployment-insights-section">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Rocket className="w-5 h-5 text-cyan-400" />
          Deployment Insights
          <span className="text-xs text-white/60 font-normal ml-2">
            Last {k.window_days || 30} days · refreshed hourly
          </span>
        </h2>
        <button
          type="button"
          onClick={() => navigate('/deployments')}
          className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
          data-testid="deployment-insights-open-deployments"
        >
          Open deployments <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* KPI strip */}
      <div
        className="alice-kpi-row grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        data-testid="deployment-insights-kpis"
      >
        <KPI
          label="Success rate"
          value={`${k.success_rate_pct ?? 0}%`}
          sub={`${k.installed || 0} installed / ${(k.installed || 0) + (k.failed || 0)} attempted`}
          icon={CheckCircle2}
          accent={ACCENT.emerald}
          testid="kpi-deploy-success-rate"
        />
        <KPI
          label="Failed devices"
          value={k.failed || 0}
          sub="Across all deployed apps"
          icon={AlertTriangle}
          accent={ACCENT.rose}
          testid="kpi-deploy-failed"
        />
        <KPI
          label="Pending"
          value={k.pending || 0}
          sub={`${k.total_devices || 0} total targeted`}
          icon={Clock}
          accent={ACCENT.amber}
          testid="kpi-deploy-pending"
        />
        <KPI
          label="Top failure reason"
          value={breakdown[0]?.count ?? 0}
          sub={topFailureLabel}
          icon={ShieldAlert}
          accent={ACCENT.purple}
          testid="kpi-top-failure"
        />
      </div>

      {/* Charts: trend + failure reasons */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2 bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <TrendingUp className="w-4 h-4 text-cyan-400" />
              Deployments per day
              <span className="text-xs text-white/60 font-normal ml-2">
                {k.deployments_in_window || 0} in window
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {series.length ? (
              <>
                <TrendBars series={series} />
                <div className="flex justify-between text-[10px] text-white/50 mt-2">
                  <span>{series[0]?.date}</span>
                  <span>{series[Math.floor(series.length / 2)]?.date}</span>
                  <span>{series[series.length - 1]?.date}</span>
                </div>
              </>
            ) : (
              <p className="text-white/60 text-sm">No deployment activity yet.</p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <BarChart3 className="w-4 h-4 text-rose-400" />
              Top failure reasons
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {breakdown.length ? (
              breakdown.slice(0, 6).map((item) => (
                <FailureBar
                  key={item.category}
                  item={item}
                  max={failureMax}
                  onClick={() => navigate(`/deployments?category=${item.category}`)}
                />
              ))
            ) : (
              <p className="text-white/60 text-sm">
                No classified failures yet. The hourly scheduler will populate this
                as soon as Intune reports errors.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Apps with highest failure rate */}
      <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            Apps by failure rate
            <span className="text-xs text-white/60 font-normal ml-2">
              Min. 3 install attempts
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {offenders.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="apps-by-failure-table">
                <thead>
                  <tr className="text-left text-xs uppercase text-white/60 border-b border-[rgba(229,239,250,0.1)]">
                    <th className="px-5 py-3 font-medium">App</th>
                    <th className="px-5 py-3 font-medium">Publisher</th>
                    <th className="px-5 py-3 font-medium text-right">Installed</th>
                    <th className="px-5 py-3 font-medium text-right">Failed</th>
                    <th className="px-5 py-3 font-medium text-right">Failure rate</th>
                  </tr>
                </thead>
                <tbody>
                  {offenders.map((a) => (
                    <tr
                      key={a.package_id}
                      className="border-b border-[rgba(229,239,250,0.05)] hover:bg-white/[0.04] transition-colors cursor-pointer"
                      onClick={() => navigate(`/deployments`)}
                      data-testid={`offender-row-${a.package_id}`}
                    >
                      <td className="px-5 py-3 text-white">{a.app_name}</td>
                      <td className="px-5 py-3 text-white/70">{a.publisher || '—'}</td>
                      <td className="px-5 py-3 text-white/80 text-right">{a.installed}</td>
                      <td className="px-5 py-3 text-rose-400 text-right">{a.failed}</td>
                      <td className="px-5 py-3 text-right">
                        <span className="text-amber-400 font-medium">
                          {a.failure_rate_pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-white/60 text-sm px-5 py-6">
              No apps with enough install attempts to rank yet.
            </p>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default DeploymentInsights;
